/** @file Observable app store. Extended in Phase 2 with tokens/drawings/tool state. */
import { createInitialTokens, createInitialScene, DRAW_COLORS } from "./play.js";

/**
 * @template T
 * @typedef {object} Store
 * @property {() => T} get
 * @property {(update: Partial<T> | ((prev: T) => Partial<T>)) => void} set
 * @property {(fn: (next: T, prev: T) => void) => () => void} subscribe
 */

/**
 * @template T
 * @param {T} initial
 * @returns {Store<T>}
 */
export function createStore(initial) {
  let current = initial;
  /** @type {Set<(next: T, prev: T) => void>} */
  const listeners = new Set();

  return {
    get: () => current,
    set: (update) => {
      const prev = current;
      const patch = typeof update === "function" ? update(prev) : update;
      current = { ...prev, ...patch };
      for (const fn of listeners) {
        fn(current, prev);
      }
    },
    subscribe: (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
  };
}

/**
 * @typedef {object} AppState
 * @property {import("./coords.js").FieldTransform | null} transform
 * @property {import("./play.js").Token[]} tokens
 * @property {import("./play.js").Drawing[]} drawings
 * @property {string | null} selectedDrawingId
 * @property {import("./play.js").ToolMode} tool
 * @property {string} drawColor
 * @property {import("./play.js").Scene[]} scenes
 * @property {number} activeSceneIndex
 */

/** Global app store. */
const _initialTokens = createInitialTokens();
export const appStore = createStore(
  /** @type {AppState} */ ({
    transform: null,
    tokens: _initialTokens,
    drawings: [],
    selectedDrawingId: null,
    tool: "select",
    drawColor: DRAW_COLORS[0],
    scenes: [createInitialScene(_initialTokens)],
    activeSceneIndex: 0,
  }),
);
